import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Tenant-user auth (Sprint 3 RBAC).
 *
 * A deliberately separate surface from `store/platformAuth.js`. Session
 * identity now lives in an httpOnly `sp_tenant_session` cookie the browser
 * sends automatically (see `backend/api/routes_tenant_auth.py`) — the raw JWT
 * is no longer kept in this store or in localStorage. What IS kept
 * client-side:
 *
 *  - `csrfToken`: the `csrf_token` value from the login response body, echoed
 *    back as `X-CSRF-Token` on every mutating request (`lib/tenantUserApi.js`).
 *    Not a secret in the XSS sense — it is already readable from its own
 *    non-httpOnly `sp_tenant_csrf` cookie — so plain persisted state is fine.
 *  - `claims`: decoded from the `access_token` the login body still returns
 *    (backward-compat value, never sent anywhere from here) purely to drive
 *    UI gating (show/hide the invite/role/remove controls on the Team page,
 *    show who's signed in). Not a verification step — the backend is the
 *    real enforcement point for every mutating call, off the cookie.
 *
 * Persisted so a reload does not force a re-login. Losing `token` here is
 * fine: the real session lives in the httpOnly cookie, which a reload
 * doesn't clear.
 */

const STORE_NAME = 'socialpulse.tenant.auth'

/**
 * Decode a JWT's payload without any signature verification — a JWT payload
 * is just base64url(JSON), so no dependency is needed for this. Returns
 * `null` for a malformed/missing token rather than throwing, since this is
 * only ever used for optimistic UI gating.
 */
function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const binary = atob(padded)
    const json = decodeURIComponent(
      Array.from(binary, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
    )
    return JSON.parse(json)
  } catch {
    return null
  }
}

function claimsFromToken(token) {
  const payload = decodeJwtPayload(token)
  if (!payload) return null
  return {
    userId: payload.user_id ?? payload.sub ?? null,
    tenantId: payload.tenant_id ?? null,
    role: payload.role ?? null,
    email: payload.email ?? null,
  }
}

export const useTenantAuth = create(
  persist(
    (set) => ({
      csrfToken: null,
      claims: null, // {userId, tenantId, role, email} — decoded from the login body, UI-gating only.
      // Display name of the active tenant (`Tenant.name`, set by a superadmin
      // at onboarding — see `db.models.tenant.Tenant`). NOT in the JWT itself
      // (a display label doesn't belong in an auth token, and would go stale
      // there if the tenant were ever renamed); sourced from the login
      // response body's `tenant_name` field instead. `null` until a session
      // exists.
      tenantName: null,
      // Whether a session is believed to exist at all (the httpOnly cookie
      // itself is invisible to JS, so this is the client's best proxy for it —
      // set on a successful login, cleared on logout / an authoritative 401).
      authenticated: false,

      // Sprint 7 tenant branding (`GET /tenant/branding`), fetched once per
      // session by `hooks/useTenantConfig.js`'s `useTenantBrandingBootstrap`.
      // `null` fields mean "use the product default" — never persisted here
      // beyond the session, since it is just a display-time application of
      // whatever `GET /tenant/branding` last reported.
      branding: { logoUrl: null, primaryColor: null, secondaryColor: null },

      /** Called on a successful `POST /tenant/auth/login`. */
      setSession: (accessToken, csrfToken, tenantName) =>
        set({
          csrfToken,
          claims: claimsFromToken(accessToken),
          tenantName: tenantName ?? null,
          authenticated: true,
        }),

      /** Called on logout, or when a request comes back 401. */
      logout: () => set({
        csrfToken: null,
        claims: null,
        tenantName: null,
        authenticated: false,
        branding: { logoUrl: null, primaryColor: null, secondaryColor: null },
      }),

      /** Called once per session by `useTenantBrandingBootstrap` on a successful `GET /tenant/branding`. */
      setBranding: (branding) => set({
        branding: {
          logoUrl: branding?.logo_url ?? null,
          primaryColor: branding?.primary_color ?? null,
          secondaryColor: branding?.secondary_color ?? null,
        },
      }),

      /**
       * Called on a successful `GET /tenant/auth/me` — self-heals display
       * data that was only ever captured once at login (see
       * `hooks/useTenantUsers.js`'s `useTenantSessionRefresh`), e.g. a
       * `tenant_name` the server looked up fresh from the DB after a rename,
       * or a role changed by another admin mid-session. Deliberately leaves
       * `csrfToken` untouched — that's session-transport state, unrelated to
       * this display-data refresh.
       */
      refreshSession: ({ tenant_id, tenant_name, role, email } = {}) =>
        set((state) => ({
          tenantName: tenant_name ?? state.tenantName,
          claims: {
            userId: state.claims?.userId ?? null,
            tenantId: tenant_id ?? state.claims?.tenantId ?? null,
            role: role ?? state.claims?.role ?? null,
            email: email ?? state.claims?.email ?? null,
          },
        })),
    }),
    { name: STORE_NAME, version: 1 },
  ),
)

/** Non-hook read for use outside components (the API client, route loaders). */
export function getTenantCsrfToken() {
  return useTenantAuth.getState().csrfToken
}

export function isTenantAuthenticated() {
  return Boolean(useTenantAuth.getState().authenticated)
}

/** The signed-in tenant user's role (`tenant_admin` / `analyst` / `viewer`), or `null`. */
export function getTenantRole() {
  return useTenantAuth.getState().claims?.role ?? null
}

export { decodeJwtPayload }
